import { useEffect, useMemo, useState } from "react";

import { getLaunchTokens } from "@/features/launch/constants";
import { useHomepageSsr } from "@/providers/HomepageSsrProvider";
import {
  type LaunchScheduleEntry,
  type LaunchScheduleRegistrations,
  type LaunchScheduleRegistrationsCopy,
} from "@/features/launch/types";

type ScheduleCopy = {
  registrations: LaunchScheduleRegistrations;
  entries?: LaunchScheduleEntry[];
};

type UseLaunchScheduleArgs = {
  scheduleCopy: ScheduleCopy;
  contentEventDate?: string | number | Date | null;
  liveSessionStartMs?: number;
  liveSessionStartDate?: Date;
  liveSessionAttendeeCount: number;
  serialSessionStartDates?: Date[];
};

export type LaunchSerialScheduleEntry = {
  startDate: Date;
  dateLabel?: string;
  timeLabel?: string;
};

type UseLaunchScheduleResult = {
  countdownTargetMs?: number;
  formattedLaunchDate?: string;
  formattedLaunchTime?: string;
  resolvedLaunchDate?: Date;
  scheduleRegistrations: LaunchScheduleRegistrationsCopy;
  scheduleEntries: LaunchScheduleEntry[];
  serialScheduleEntries: LaunchSerialScheduleEntry[];
};

const { colors: baseLaunchColors } = getLaunchTokens();

const capitalizeFirstLetter = (text: string): string =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

const normalizeDayPeriod = (value: string): string =>
  value.replace(/[.\s\u202F]/g, "").toLowerCase();

const stripTimeLeadingZero = (label: string): string =>
  label.replace(/^0(?=\d:\d{2}(?:\s*[ap]\.?m\.?)?$)/i, "");

const formatLaunchDateLabel = (
  date: Date,
  timeZone?: string
): string | undefined => {
  const formatter = new Intl.DateTimeFormat("es-ES", {
    weekday: "long",
    day: "numeric",
    month: "long",
    ...(timeZone ? { timeZone } : {}),
  });

  const parts = formatter.formatToParts(date);
  const weekday = parts.find((part) => part.type === "weekday")?.value ?? "";
  const day = parts.find((part) => part.type === "day")?.value ?? "";
  const month = parts.find((part) => part.type === "month")?.value ?? "";

  if (!day || !month) {
    return undefined;
  }

  const normalizedWeekday = capitalizeFirstLetter(weekday);
  const dateSegment = `${day} de ${month}`;

  return normalizedWeekday
    ? `${normalizedWeekday} ${dateSegment}`
    : dateSegment;
};

const formatLaunchTimeLabel = (
  date: Date,
  timeZone?: string
): string | undefined => {
  const formatter = new Intl.DateTimeFormat("es-ES", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    ...(timeZone ? { timeZone } : {}),
  });

  const parts = formatter.formatToParts(date);

  if (!parts.length) {
    const formatted = formatter.format(date);
    const normalized = formatted.replace(
      /([ap])\.?\s*m\.?/gi,
      (_, meridiem: string) => `${meridiem.toLowerCase()}m`
    );
    return stripTimeLeadingZero(normalized);
  }

  const formattedTime = parts
    .map((part) => {
      if (part.type === "dayPeriod") {
        return normalizeDayPeriod(part.value);
      }
      if (part.type === "literal" && /\s/.test(part.value)) {
        return " ";
      }
      return part.value;
    })
    .join("")
    .trim();

  return stripTimeLeadingZero(formattedTime);
};

const formatSerialDateLabel = (
  date: Date,
  timeZone?: string
): string | undefined => {
  const formatter = new Intl.DateTimeFormat("es-ES", {
    weekday: "short",
    day: "numeric",
    month: "short",
    ...(timeZone ? { timeZone } : {}),
  });

  const parts = formatter.formatToParts(date);
  const clean = (value: string) =>
    capitalizeFirstLetter(value.replace(/\./g, "").trim());
  const weekday = clean(
    parts.find((part) => part.type === "weekday")?.value ?? ""
  );
  const day = parts.find((part) => part.type === "day")?.value ?? "";
  const month = clean(parts.find((part) => part.type === "month")?.value ?? "");

  if (!day || !month) {
    return undefined;
  }
  return [weekday, day, month].filter(Boolean).join(" ");
};

const formatSerialTimeLabel = (
  date: Date,
  timeZone?: string
): string | undefined => formatLaunchTimeLabel(date, timeZone)?.toUpperCase();

const getBrowserTimeZone = (): string | undefined => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
};

const parseEventDateMs = (
  eventDate?: string | number | Date | null
): number | undefined => {
  if (!eventDate) return undefined;
  if (typeof eventDate === "number") {
    return Number.isFinite(eventDate) ? eventDate : undefined;
  }

  const parsed = Date.parse(String(eventDate));
  return Number.isNaN(parsed) ? undefined : parsed;
};

const buildScheduleEntries = (
  scheduleCopy: ScheduleCopy,
  formattedLaunchDate?: string,
  formattedLaunchTime?: string
): LaunchScheduleEntry[] => {
  const baseEntries = scheduleCopy.entries ?? [];
  const calendarSource =
    baseEntries.find((entry) => entry.icon === "calendar") ?? baseEntries[0];
  const timeSource =
    baseEntries.find((entry) => entry.icon === "clock") ?? baseEntries[1];
  const locationSource =
    baseEntries.find((entry) => entry.icon === "location") ?? baseEntries[2];

  const entries: LaunchScheduleEntry[] = [];

  const calendarLabel = formattedLaunchDate ?? calendarSource?.label ?? "";
  if (calendarLabel) {
    entries.push({
      icon: calendarSource?.icon ?? "calendar",
      color: calendarSource?.color ?? baseLaunchColors.iconCalendar,
      label: calendarLabel,
    });
  }

  const timeLabel = formattedLaunchTime ?? timeSource?.label ?? "";
  if (timeLabel) {
    entries.push({
      icon: timeSource?.icon ?? "clock",
      color: timeSource?.color ?? baseLaunchColors.iconClock,
      label: timeLabel,
    });
  }

  const locationLabel = locationSource?.label ?? "";
  if (locationLabel) {
    entries.push({
      icon: locationSource?.icon ?? "location",
      color: locationSource?.color ?? baseLaunchColors.iconLocation,
      label: locationLabel,
    });
  }

  return entries.filter((entry) => Boolean(entry.label));
};

const parseRegistrationsHighlight = (
  value: LaunchScheduleRegistrations["highlighted"]
): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
};

export const useLaunchSchedule = (
  args: UseLaunchScheduleArgs
): UseLaunchScheduleResult => {
  const { launch: ssrLaunch } = useHomepageSsr();
  const {
    scheduleCopy,
    contentEventDate,
    liveSessionStartMs,
    liveSessionStartDate,
    liveSessionAttendeeCount,
    serialSessionStartDates,
  } = args;
  const [temporalContext, setTemporalContext] = useState(() => ({
    nowMs: ssrLaunch?.renderedAtMs ?? Date.now(),
    timeZone: ssrLaunch?.serverTimeZone,
  }));

  useEffect(() => {
    if (!ssrLaunch) return;

    const nextContext = {
      nowMs: Date.now(),
      timeZone: getBrowserTimeZone(),
    };
    setTemporalContext((current) =>
      current.nowMs === nextContext.nowMs &&
      current.timeZone === nextContext.timeZone
        ? current
        : nextContext
    );
  }, [ssrLaunch]);

  const contentEventDateMs = useMemo(
    () => parseEventDateMs(contentEventDate),
    [contentEventDate]
  );

  const serialScheduleEntries = useMemo<LaunchSerialScheduleEntry[]>(
    () =>
      (serialSessionStartDates ?? []).map((startDate) => ({
        startDate,
        dateLabel: formatSerialDateLabel(startDate, temporalContext.timeZone),
        timeLabel: formatSerialTimeLabel(startDate, temporalContext.timeZone),
      })),
    [serialSessionStartDates, temporalContext.timeZone]
  );

  const nextSerialSessionDate = useMemo(() => {
    const dates = serialSessionStartDates ?? [];
    if (!dates.length) return undefined;
    return (
      dates.find((date) => date.getTime() > temporalContext.nowMs) ??
      dates[dates.length - 1]
    );
  }, [serialSessionStartDates, temporalContext.nowMs]);

  const countdownTargetMs = useMemo(
    () =>
      nextSerialSessionDate?.getTime() ??
      liveSessionStartMs ??
      contentEventDateMs,
    [contentEventDateMs, liveSessionStartMs, nextSerialSessionDate]
  );

  const resolvedLaunchDate = useMemo(() => {
    if (nextSerialSessionDate) return nextSerialSessionDate;
    if (liveSessionStartDate) return liveSessionStartDate;
    if (contentEventDateMs) {
      const eventDate = new Date(contentEventDateMs);
      return Number.isNaN(eventDate.getTime()) ? undefined : eventDate;
    }
    if (countdownTargetMs) {
      const fallbackDate = new Date(countdownTargetMs);
      return Number.isNaN(fallbackDate.getTime()) ? undefined : fallbackDate;
    }
    return undefined;
  }, [
    contentEventDateMs,
    countdownTargetMs,
    liveSessionStartDate,
    nextSerialSessionDate,
  ]);

  const formattedLaunchDate = useMemo(() => {
    if (!resolvedLaunchDate) return undefined;
    return formatLaunchDateLabel(resolvedLaunchDate, temporalContext.timeZone);
  }, [resolvedLaunchDate, temporalContext.timeZone]);

  const formattedLaunchTime = useMemo(() => {
    if (!resolvedLaunchDate) return undefined;
    return formatLaunchTimeLabel(resolvedLaunchDate, temporalContext.timeZone);
  }, [resolvedLaunchDate, temporalContext.timeZone]);

  const fallbackRegistrations = useMemo(
    () => parseRegistrationsHighlight(scheduleCopy.registrations.highlighted),
    [scheduleCopy.registrations.highlighted]
  );

  const registrationCount = useMemo(() => {
    if (Number.isFinite(liveSessionAttendeeCount)) {
      return Math.max(liveSessionAttendeeCount, 0);
    }
    if (typeof fallbackRegistrations === "number") {
      return Math.max(fallbackRegistrations, 0);
    }
    return undefined;
  }, [fallbackRegistrations, liveSessionAttendeeCount]);

  const scheduleRegistrations: LaunchScheduleRegistrationsCopy = useMemo(
    () => ({
      prefix: scheduleCopy.registrations.prefix,
      suffix: scheduleCopy.registrations.suffix,
      highlighted:
        registrationCount !== undefined
          ? `${registrationCount}`
          : String(scheduleCopy.registrations.highlighted),
    }),
    [registrationCount, scheduleCopy.registrations]
  );

  const scheduleEntries = useMemo(
    () =>
      buildScheduleEntries(
        scheduleCopy,
        formattedLaunchDate,
        formattedLaunchTime
      ),
    [formattedLaunchDate, formattedLaunchTime, scheduleCopy]
  );

  return {
    countdownTargetMs,
    formattedLaunchDate,
    formattedLaunchTime,
    resolvedLaunchDate,
    scheduleRegistrations,
    scheduleEntries,
    serialScheduleEntries,
  };
};
